import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { emptyBoard, type Board, type CanvasProjectMeta } from './canvasBoard';
import type { CanvasAsk } from './canvasAgent';

/** The generated Database types don't know the sharing tables/RPCs yet. */
const db = supabase as unknown as SupabaseClient;

export type ShareRole = 'owner' | 'edit' | 'view';

export type ShareMember = { userId: string; email: string; name: string };

export type SharePerson = {
  userId: string;
  canEdit: boolean;
  email?: string;
  name?: string;
  /** True when the account is not a member of the sharer's organization. */
  external?: boolean;
};

export type ShareSettings = {
  shareAll: boolean;
  shareAllCanEdit: boolean;
  people: SharePerson[];
  /** Include the agent conversation with the shared board. */
  shareChat?: boolean;
};

export type ShareRecipientLookup = SharePerson & { inOrg: boolean };

/** Slim chat payload for the shared copy (no busy/debit runtime state). */
export type SharedChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  refs?: string[];
  suggestions?: string[];
  credits?: number;
  inputTokens?: number;
  outputTokens?: number;
  generationCredits?: number;
  generationKind?: 'image' | 'video' | 'audio';
  error?: boolean;
  contextBreak?: boolean;
  formAnswer?: boolean;
  /** Kept so a reload can still skip a "não" keyframe question and continue the video. */
  ask?: CanvasAsk;
  askAnswers?: Record<string, string>;
};

function sharedAsk(raw: unknown): CanvasAsk | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const fields = (raw as { fields?: unknown }).fields;
  if (!Array.isArray(fields) || !fields.length || fields.length > 4) return undefined;
  let json = '';
  try { json = JSON.stringify(raw); } catch { return undefined; }
  if (json.length > 4000) return undefined;
  return raw as CanvasAsk;
}

function sharedAnswers(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== 'string') continue;
    out[key.slice(0, 40)] = value.slice(0, 40);
  }
  return Object.keys(out).length ? out : undefined;
}

export function shareableChat(messages: Array<Record<string, unknown>>): SharedChatMessage[] {
  return messages
    .filter((m) => m && (m.role === 'user' || m.role === 'assistant'))
    .slice(-40)
    .map((m) => {
      const row: SharedChatMessage = {
        id: String(m.id || ''),
        role: m.role === 'user' ? 'user' : 'assistant',
        text: String(m.text || ''),
      };
      if (Array.isArray(m.refs) && m.refs.length) row.refs = m.refs.map(String);
      if (Array.isArray(m.suggestions) && m.suggestions.length) row.suggestions = m.suggestions.map(String).slice(0, 8);
      if (typeof m.credits === 'number') row.credits = m.credits;
      if (typeof m.inputTokens === 'number') row.inputTokens = m.inputTokens;
      if (typeof m.outputTokens === 'number') row.outputTokens = m.outputTokens;
      if (typeof m.generationCredits === 'number') row.generationCredits = m.generationCredits;
      if (m.generationKind === 'image' || m.generationKind === 'video' || m.generationKind === 'audio') {
        row.generationKind = m.generationKind;
      }
      if (m.error) row.error = true;
      if (m.contextBreak) row.contextBreak = true;
      if (m.formAnswer) row.formAnswer = true;
      const ask = sharedAsk(m.ask);
      if (ask) row.ask = ask;
      const askAnswers = sharedAnswers(m.askAnswers);
      if (askAnswers) row.askAnswers = askAnswers;
      return row;
    })
    .filter((m) => m.id && (m.text || m.contextBreak));
}

export type SharedWithMe = {
  id: string;
  title: string;
  ownerId: string;
  ownerName: string;
  role: 'edit' | 'view';
  thumbUrl?: string;
  thumbKind?: 'image' | 'video';
  version: number;
  updatedAt: number;
};

export type RemoteProject = {
  board: Board;
  version: number;
  title: string;
  role: ShareRole;
  shareChat?: boolean;
  chat?: SharedChatMessage[];
};

/** Plans with a team (Empresarial) can share projects. */
export function orgCanShare(org: { plans?: { can_invite_users?: boolean | null } | null } | null | undefined): boolean {
  return Boolean(org?.plans?.can_invite_users);
}

/** Joins and mixes run in the browser tab that started them, so another tab's "joining" is never live here. */
function asBoard(raw: unknown): Board {
  const b = raw as Board | null;
  if (!b || !Array.isArray(b.nodes) || !Array.isArray(b.edges)) return emptyBoard();
  const nodes = b.nodes.map((n) => (n.kind === 'video' && n.status === 'joining'
    ? { ...n, status: n.videoUrl ? 'ready' as const : 'idle' as const, progress: undefined }
    : n));
  return { ...emptyBoard(), ...b, nodes };
}

export async function listShareMembers(orgId: string): Promise<ShareMember[]> {
  const { data, error } = await db.rpc('list_canvas_share_members', { p_org_id: orgId });
  if (error) throw new Error(error.message);
  return ((data || []) as Array<{ user_id: string; email: string | null; full_name: string | null }>).map((m) => ({
    userId: m.user_id,
    email: m.email || '',
    name: m.full_name || m.email || 'Sem nome',
  }));
}

/** Resolve an e-mail to a shareable account (same org or external). */
export async function lookupShareRecipient(orgId: string, email: string): Promise<ShareRecipientLookup | null> {
  const { data, error } = await db.rpc('lookup_canvas_share_recipient', {
    p_org_id: orgId,
    p_email: email.trim(),
  });
  if (error) throw new Error(error.message);
  const row = Array.isArray(data) ? data[0] as {
    user_id?: string;
    email?: string | null;
    full_name?: string | null;
    in_org?: boolean;
  } | undefined : undefined;
  if (!row?.user_id) return null;
  const mail = String(row.email || email).trim();
  const inOrg = Boolean(row.in_org);
  return {
    userId: row.user_id,
    email: mail,
    name: String(row.full_name || mail),
    canEdit: false,
    inOrg,
    external: !inOrg,
  };
}

/** Current sharing of a project the caller owns; null when it isn't shared. */
export async function getShareSettings(projectId: string): Promise<ShareSettings | null> {
  const { data: project, error } = await db
    .from('canvas_projects')
    .select('share_all, share_all_can_edit, share_chat')
    .eq('id', projectId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!project) return null;
  const { data: rows, error: sharesError } = await db.rpc('list_canvas_project_share_people', {
    p_project_id: projectId,
  });
  if (sharesError) throw new Error(sharesError.message);
  const people = ((rows || []) as Array<{
    user_id: string;
    email: string | null;
    full_name: string | null;
    can_edit: boolean;
    in_org: boolean;
  }>).map((r) => ({
    userId: r.user_id,
    canEdit: r.can_edit,
    email: r.email || '',
    name: r.full_name || r.email || 'Pessoa',
    external: !r.in_org,
  }));
  // Private cloud copies exist without sharing — don't treat them as shared.
  if (!project.share_all && !people.length) return null;
  return {
    shareAll: Boolean(project.share_all),
    shareAllCanEdit: Boolean(project.share_all_can_edit),
    shareChat: Boolean(project.share_chat),
    people,
  };
}

/** E-mails newly shared people after a successful share_canvas_project. */
export async function notifyCanvasShare(args: {
  projectId: string;
  title: string;
  recipients: Array<{ userId: string; canEdit: boolean }>;
}): Promise<number> {
  if (!args.recipients.length) return 0;
  const { data, error } = await supabase.functions.invoke('notify-canvas-share', {
    body: {
      project_id: args.projectId,
      title: args.title,
      recipients: args.recipients.map((r) => ({ user_id: r.userId, can_edit: r.canEdit })),
    },
  });
  if (error) throw new Error(error.message);
  return Number((data as { sent?: number } | null)?.sent) || 0;
}

/** Creates/updates sharing. Unshare clears flags but keeps the owner's cloud row. */
export async function shareProject(args: {
  projectId: string;
  orgId: string;
  meta: CanvasProjectMeta;
  board: Board;
  settings: ShareSettings;
  chat?: SharedChatMessage[];
}): Promise<number> {
  const shareChat = Boolean(args.settings.shareChat);
  const { data, error } = await db.rpc('share_canvas_project', {
    p_project_id: args.projectId,
    p_org_id: args.orgId,
    p_title: args.meta.title,
    p_board: args.board,
    p_thumb_url: args.meta.thumbUrl || null,
    p_thumb_kind: args.meta.thumbKind || null,
    p_share_all: args.settings.shareAll,
    p_share_all_can_edit: args.settings.shareAllCanEdit,
    p_people: args.settings.people.map((p) => ({ user_id: p.userId, can_edit: p.canEdit })),
    p_share_chat: shareChat,
    p_chat: shareChat ? (args.chat || []) : null,
  });
  if (error) throw new Error(error.message);
  return Number(data) || 0;
}

/** New version, or null when someone else saved since `baseVersion`. */
export async function saveSharedBoard(
  projectId: string,
  board: Board,
  meta: Pick<CanvasProjectMeta, 'title' | 'thumbUrl' | 'thumbKind'> | undefined,
  baseVersion: number,
  chat?: SharedChatMessage[] | null,
): Promise<number | null> {
  const { data, error } = await db.rpc('save_canvas_project', {
    p_project_id: projectId,
    p_board: board,
    p_title: meta?.title || null,
    p_thumb_url: meta?.thumbUrl || null,
    p_thumb_kind: meta?.thumbKind || null,
    p_base_version: baseVersion,
    ...(chat ? { p_chat: chat } : {}),
  });
  if (error) throw new Error(error.message);
  return data == null ? null : Number(data);
}

function asSharedChat(raw: unknown): SharedChatMessage[] | undefined {
  if (!Array.isArray(raw) || !raw.length) return undefined;
  return shareableChat(raw as Array<Record<string, unknown>>);
}

/** The shared board and the caller's role; null when it was unshared or access was removed. */
export async function fetchSharedProject(projectId: string): Promise<RemoteProject | null> {
  const [{ data, error }, roleRes] = await Promise.all([
    db.from('canvas_projects').select('board, version, title, share_chat, chat').eq('id', projectId).maybeSingle(),
    db.rpc('canvas_project_role', { p_project_id: projectId }),
  ]);
  if (error) throw new Error(error.message);
  const role = roleRes.data as ShareRole | null;
  if (!data || !role) return null;
  const chat = Boolean(data.share_chat) ? asSharedChat(data.chat) : undefined;
  return {
    board: asBoard(data.board),
    version: Number(data.version) || 1,
    title: String(data.title || ''),
    role,
    shareChat: Boolean(data.share_chat),
    ...(chat ? { chat } : {}),
  };
}

export async function fetchSharedVersion(projectId: string): Promise<number | null> {
  const { data, error } = await db.from('canvas_projects').select('version').eq('id', projectId).maybeSingle();
  if (error) throw new Error(error.message);
  return data ? Number(data.version) || 1 : null;
}

export async function listSharedWithMe(): Promise<SharedWithMe[]> {
  const { data, error } = await db.rpc('list_shared_canvas_projects');
  if (error) throw new Error(error.message);
  return ((data || []) as Array<Record<string, unknown>>).map((r) => ({
    id: String(r.id),
    title: String(r.title || 'Projeto sem título'),
    ownerId: String(r.owner_id),
    ownerName: String(r.owner_name || 'Alguém da empresa'),
    role: r.role === 'edit' ? 'edit' : 'view',
    thumbUrl: r.thumb_url ? String(r.thumb_url) : undefined,
    thumbKind: r.thumb_kind === 'video' ? 'video' : r.thumb_kind === 'image' ? 'image' : undefined,
    version: Number(r.version) || 1,
    updatedAt: r.updated_at ? new Date(String(r.updated_at)).getTime() : Date.now(),
  }));
}

/** Meta of projects this user owns in the org (shared or private cloud copies). */
export type OwnCloudProject = {
  id: string;
  title: string;
  customTitle: boolean;
  thumbUrl?: string;
  thumbKind?: 'image' | 'video';
  scenes: number;
  videos: number;
  shared: boolean;
  version: number;
  createdAt: number;
  updatedAt: number;
};

export type OwnCloudProjectFull = OwnCloudProject & {
  board: Board;
  ownerChat?: unknown;
};

function mapOwnMeta(r: Record<string, unknown>): OwnCloudProject {
  return {
    id: String(r.id),
    title: String(r.title || 'Projeto sem título'),
    customTitle: Boolean(r.custom_title),
    thumbUrl: r.thumb_url ? String(r.thumb_url) : undefined,
    thumbKind: r.thumb_kind === 'video' ? 'video' : r.thumb_kind === 'image' ? 'image' : undefined,
    scenes: Number(r.scenes) || 0,
    videos: Number(r.videos) || 0,
    shared: Boolean(r.shared),
    version: Number(r.version) || 1,
    createdAt: r.created_at ? new Date(String(r.created_at)).getTime() : Date.now(),
    updatedAt: r.updated_at ? new Date(String(r.updated_at)).getTime() : Date.now(),
  };
}

export async function listOwnProjects(orgId: string): Promise<OwnCloudProject[]> {
  const { data, error } = await db.rpc('list_own_canvas_projects', { p_org_id: orgId });
  if (error) throw new Error(error.message);
  return ((data || []) as Array<Record<string, unknown>>).map(mapOwnMeta);
}

export async function fetchOwnProject(projectId: string): Promise<OwnCloudProjectFull | null> {
  const { data, error } = await db.rpc('fetch_own_canvas_project', { p_project_id: projectId });
  if (error) throw new Error(error.message);
  const row = Array.isArray(data) ? data[0] as Record<string, unknown> | undefined : undefined;
  if (!row?.id) return null;
  return {
    ...mapOwnMeta(row),
    board: asBoard(row.board),
    ownerChat: row.owner_chat ?? undefined,
  };
}

/** Upserts the owner's cloud copy (works for private and shared projects). */
export async function upsertOwnProject(args: {
  projectId: string;
  orgId: string;
  meta: CanvasProjectMeta;
  board: Board;
  ownerChat?: unknown;
}): Promise<number> {
  const { data, error } = await db.rpc('upsert_own_canvas_project', {
    p_project_id: args.projectId,
    p_org_id: args.orgId,
    p_title: args.meta.title,
    p_board: args.board,
    p_thumb_url: args.meta.thumbUrl || null,
    p_thumb_kind: args.meta.thumbKind || null,
    p_custom_title: Boolean(args.meta.customTitle),
    p_scenes: args.meta.scenes || 0,
    p_videos: args.meta.videos || 0,
    p_owner_chat: args.ownerChat ?? null,
  });
  if (error) throw new Error(error.message);
  return Number(data) || 0;
}

export async function deleteOwnProject(projectId: string): Promise<boolean> {
  const { data, error } = await db.rpc('delete_own_canvas_project', { p_project_id: projectId });
  if (error) throw new Error(error.message);
  return Boolean(data);
}

export function ownMetaToLocal(remote: OwnCloudProject): CanvasProjectMeta {
  return {
    id: remote.id,
    title: remote.title,
    customTitle: remote.customTitle || undefined,
    createdAt: remote.createdAt,
    updatedAt: remote.updatedAt,
    thumbUrl: remote.thumbUrl,
    thumbKind: remote.thumbKind,
    scenes: remote.scenes,
    videos: remote.videos,
    shared: remote.shared || undefined,
  };
}

/** Calls `onChange(version)` whenever someone saves the shared board. */
export function subscribeSharedProject(projectId: string, onChange: (version: number) => void): () => void {
  const channel = db
    .channel(`canvas-project-${projectId}`)
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'canvas_projects', filter: `id=eq.${projectId}` },
      // The row carries the whole board, which can exceed the Realtime payload limit: only trust the version.
      (payload) => onChange(Number((payload.new as { version?: number })?.version) || 0),
    )
    .subscribe();
  return () => {
    void db.removeChannel(channel);
  };
}

/** Key-order-insensitive JSON: Postgres jsonb reorders keys, so plain JSON.stringify would see changes that aren't. */
function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value as Record<string, unknown>)
      .filter((k) => (value as Record<string, unknown>)[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableJson((value as Record<string, unknown>)[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

const same = (a: unknown, b: unknown) => a === b || stableJson(a) === stableJson(b);

export const sameBoard = (a: Board, b: Board) => same(a, b);

function mergeById<T extends { id: string }>(base: T[], local: T[], remote: T[]): T[] {
  const b = new Map(base.map((x) => [x.id, x]));
  const l = new Map(local.map((x) => [x.id, x]));
  const r = new Map(remote.map((x) => [x.id, x]));
  const ids = Array.from(new Set([...remote.map((x) => x.id), ...local.map((x) => x.id)]));
  const out: T[] = [];
  for (const id of ids) {
    const mine = l.get(id);
    if (!same(mine, b.get(id))) {
      if (mine) out.push(mine);
      continue;
    }
    const theirs = r.get(id);
    if (theirs) out.push(theirs);
  }
  return out;
}

/**
 * Three-way merge of a shared board: whatever changed locally since the last sync (node, edge or
 * board setting) wins; everything else takes the remote state, including remote additions/removals.
 */
export function mergeBoards(base: Board, local: Board, remote: Board): Board {
  if (same(local, base)) return remote;
  const settings = (x: Board) => ({ aspectRatio: x.aspectRatio, aspectChosen: x.aspectChosen, imageModelId: x.imageModelId });
  const nodes = mergeById(base.nodes, local.nodes, remote.nodes);
  const ids = new Set(nodes.map((n) => n.id));
  const edges = mergeById(base.edges, local.edges, remote.edges).filter((e) => ids.has(e.from) && ids.has(e.to));
  return {
    ...(same(settings(local), settings(base)) ? settings(remote) : settings(local)),
    nodes,
    edges,
  };
}
